import { useEffect, useRef, useState, useCallback } from 'react';
import { useTikTokVideoId } from '../../hooks/useTikTokVideoId';

interface VerticalVideoModeProps {
  companies: Array<{
    id: number;
    name: string;
    industry: string;
    videoThumbnail: string;
    editorNote: string;
    tags: string[];
    tiktokVideoId?: string;
    tiktokEmbedUrl?: string;
  }>;
  onClose: () => void;
  onIndexChange?: (index: number) => void;
}

let currentPlayingIframe: HTMLIFrameElement | null = null;

const HEADER_HEIGHT = 100;

export default function VerticalVideoMode({ companies, onClose, onIndexChange }: VerticalVideoModeProps) {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isTransitioning, setIsTransitioning] = useState(false);

  const iframeRef = useRef<HTMLIFrameElement>(null);
  const isTransitioningRef = useRef(false);
  const currentIndexRef = useRef(0);

  useEffect(() => {
    isTransitioningRef.current = isTransitioning;
  }, [isTransitioning]);

  useEffect(() => {
    currentIndexRef.current = currentIndex;
    onIndexChange?.(currentIndex);
  }, [currentIndex, onIndexChange]);

  const currentCompany = companies[currentIndex];

  const needsResolution = !currentCompany.tiktokVideoId && !!currentCompany.tiktokEmbedUrl;
  const { videoId: resolvedVideoId } = useTikTokVideoId(needsResolution ? (currentCompany.tiktokEmbedUrl ?? '') : '');

  const effectiveVideoId = currentCompany.tiktokVideoId || resolvedVideoId;
  const embedUrl = effectiveVideoId
    ? `https://www.tiktok.com/player/v1/${effectiveVideoId}?controls=1&play_button=1&music_info=0&description=0`
    : null;

  const pauseVideo = useCallback(() => {
    if (!iframeRef.current) return;
    iframeRef.current.contentWindow?.postMessage(
      { type: 'pause', value: null, 'x-tiktok-player': true }, '*'
    );
    if (currentPlayingIframe === iframeRef.current) currentPlayingIframe = null;
  }, []);

  const triggerTransition = useCallback((newIndex: number) => {
    if (isTransitioningRef.current) return;
    isTransitioningRef.current = true;
    setIsTransitioning(true);
    pauseVideo();
    setCurrentIndex(newIndex);
    setTimeout(() => {
      isTransitioningRef.current = false;
      setIsTransitioning(false);
    }, 300);
  }, [pauseVideo]);

  const goToPrevious = useCallback(() => {
    const idx = currentIndexRef.current;
    if (idx > 0 && !isTransitioningRef.current) {
      triggerTransition(idx - 1);
    }
  }, [triggerTransition]);

  const goToNext = useCallback(() => {
    const idx = currentIndexRef.current;
    if (idx < companies.length - 1 && !isTransitioningRef.current) {
      triggerTransition(idx + 1);
    }
  }, [companies.length, triggerTransition]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowUp' || e.key === 'ArrowLeft') { e.preventDefault(); goToPrevious(); }
      else if (e.key === 'ArrowDown' || e.key === 'ArrowRight') { e.preventDefault(); goToNext(); }
      else if (e.key === 'Escape') { onClose(); }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToNext, goToPrevious, onClose]);

  const canGoPrev = currentIndex > 0;
  const canGoNext = currentIndex < companies.length - 1;

  return (
    <div
      className="fixed left-0 right-0 bottom-0 bg-black z-40 flex items-center justify-center overflow-hidden"
      style={{ top: `${HEADER_HEIGHT}px` }}
    >
      {/* 動画コンテナ: 9:16 維持・高さ優先・最大化 */}
      <div
        className="relative bg-black"
        style={{
          height: `min(calc(100dvh - ${HEADER_HEIGHT}px), calc(100vw * 16 / 9))`,
          width: `min(100vw, calc((100dvh - ${HEADER_HEIGHT}px) * 9 / 16))`,
          aspectRatio: '9 / 16',
          opacity: isTransitioning ? 0.7 : 1,
          transition: 'opacity 0.3s ease',
        }}
      >
        {/* iframe — オーバーレイなし、直接クリック可能 */}
        {embedUrl ? (
          <iframe
            ref={iframeRef}
            key={embedUrl}
            src={embedUrl}
            className="absolute inset-0 w-full h-full"
            allow="autoplay; fullscreen"
            allowFullScreen
            loading="lazy"
            style={{ border: 'none' }}
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center">
            <p className="text-white text-sm">動画を読み込めませんでした</p>
          </div>
        )}

        {/* ── 上部オーバーレイ: カウンター + 上ボタン ── */}
        <div className="absolute top-0 left-0 right-0 flex items-center justify-between pt-4 px-4 z-20 pointer-events-none">
          <div className="w-11" />
          <span className="bg-black/50 backdrop-blur-md text-white text-xs px-4 py-1.5 rounded-full">
            {currentIndex + 1} / {companies.length}
          </span>
          {/* 上へボタン */}
          <button
            onClick={goToPrevious}
            disabled={!canGoPrev}
            className={`pointer-events-auto w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer
              ${!canGoPrev
                ? 'bg-white/10 text-white/30 cursor-not-allowed'
                : 'bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 hover:scale-110'
              }`}
            aria-label="前の動画"
          >
            <i className="ri-arrow-up-s-line text-2xl"></i>
          </button>
        </div>

        {/* ── 下部オーバーレイ: 企業情報 + インジケーター + 下ボタン ── */}
        <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/95 via-black/70 to-transparent p-5 z-20 pointer-events-none">
          {/* 企業情報 */}
          <div>
            <div className="mb-2">
              <span className="inline-block bg-white/20 backdrop-blur-md px-3 py-1 rounded-full text-xs text-white font-medium">
                {currentCompany.industry}
              </span>
            </div>
            <h3 className="text-white text-lg font-bold mb-1">{currentCompany.name}</h3>
            <p className="text-white/85 text-xs mb-3 line-clamp-2 leading-relaxed">{currentCompany.editorNote}</p>
            <div className="flex flex-wrap gap-1.5 mb-4">
              {currentCompany.tags.slice(0, 3).map((tag, idx) => (
                <span key={idx} className="text-xs text-white/80 bg-white/10 backdrop-blur-sm px-2 py-0.5 rounded-md">
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* インジケーター + 下ボタン */}
          <div className="flex items-center justify-between gap-3">
            {/* 下へボタン */}
            <button
              onClick={goToNext}
              disabled={!canGoNext}
              className={`pointer-events-auto w-11 h-11 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer flex-shrink-0
                ${!canGoNext
                  ? 'bg-white/10 text-white/30 cursor-not-allowed'
                  : 'bg-black/50 backdrop-blur-sm text-white hover:bg-black/70 hover:scale-110'
                }`}
              aria-label="次の動画"
            >
              <i className="ri-arrow-down-s-line text-2xl"></i>
            </button>

            {/* インジケーター */}
            <div className="flex items-center justify-center gap-1.5 flex-1 pointer-events-auto">
              {companies.map((_, idx) => (
                <button
                  key={idx}
                  onClick={() => {
                    if (!isTransitioning) triggerTransition(idx);
                  }}
                  className={`h-1 rounded-full transition-all duration-300 cursor-pointer ${
                    idx === currentIndex ? 'w-6 bg-white' : 'w-1 bg-white/40 hover:bg-white/60'
                  }`}
                />
              ))}
            </div>

            <div className="w-11" />
          </div>
        </div>
      </div>
    </div>
  );
}
